import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { messageThreadApi } from '../services/api';
import type { ConversationSubject } from './MessageConversation';

interface MessageExportDialogProps {
  projectId: string;
  projectLabel: string;
  /** The project's subjects; the one shown in the tab is chosen first. */
  subjects: ConversationSubject[];
  chosenSubject?: string;
  onClose: () => void;
}

/**
 * "Exportera" on Meddelanden (ClickUp 869fjkmyw): the whole project's
 * conversation or one subject, as a PDF – with the attached files in a ZIP by
 * default, which is what a lawyer or a dispute needs.
 */
export function MessageExportDialog({ projectId, projectLabel, subjects, chosenSubject, onClose }: MessageExportDialogProps) {
  const { t } = useTranslation();
  const [scope, setScope] = useState<string>(chosenSubject && chosenSubject !== 'all' ? chosenSubject : 'project');
  const [attachments, setAttachments] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const [threadType, threadId] = scope.split(':') as ['milestone' | 'contract' | 'project', string];
      await messageThreadApi.exportProjectMessages(projectId, {
        ...(scope !== 'project' ? { thread: { threadType, threadId } } : {}),
        attachments,
      });
      onClose();
    } catch {
      setError(t('messageExport.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t('messageExport.title', { project: projectLabel })}
      onClose={onClose}
      fullscreenBelow="sm"
      footer={<>
        <button type="button" className="btn btn-light" onClick={onClose} disabled={busy}>{t('common.cancel')}</button>
        <button type="button" className="btn btn-primary" onClick={() => void download()} disabled={busy} data-testid="message-export-download">
          <i className="feather-download me-2" aria-hidden="true"></i>
          {t(busy ? 'messageExport.preparing' : 'messageExport.download')}
        </button>
      </>}
    >
      <fieldset className="message-export-scope">
        <legend className="form-label fw-semibold">{t('messageExport.scope')}</legend>
        <label className="message-export-option">
          <input type="radio" className="form-check-input" name="message-export-scope" checked={scope === 'project'} onChange={() => setScope('project')} />
          <span><strong>{t('messageExport.wholeProject')}</strong><small>{t('messageExport.wholeProjectHint')}</small></span>
        </label>
        {subjects.map(subject => (
          <label key={subject.key} className="message-export-option">
            <input type="radio" className="form-check-input" name="message-export-scope" checked={scope === subject.key}
              onChange={() => setScope(subject.key)} />
            <span><strong>{t('messageExport.onlySubject', { subject: subject.label })}</strong></span>
          </label>
        ))}
      </fieldset>
      <label className="message-export-option is-attachments">
        <input type="checkbox" className="form-check-input" checked={attachments} onChange={event => setAttachments(event.target.checked)}
          data-testid="message-export-attachments" />
        <span><strong>{t('messageExport.attachments')}</strong><small>{t(attachments ? 'messageExport.attachmentsHintZip' : 'messageExport.attachmentsHintPdf')}</small></span>
      </label>
      <p className="text-muted small mb-0">{t('messageExport.logged')}</p>
      {error && <div className="alert alert-danger mt-3 mb-0" role="alert">{error}</div>}
    </Modal>
  );
}
